import React, { useMemo } from "react";
import { LIFECYCLE } from "@/lib/constants";

/**
 * The Convergence stepper.
 * Two SVG tracks (claimant top, respondent bottom) curve toward the centre line
 * and fully merge at the resolved step. As the case progresses, a clay overlay
 * "draws" along each track up to the current status — animated with stroke-dashoffset.
 */
export default function CaseTimeline({ currentStatus, events = [] }) {
  const idx = Math.max(0, LIFECYCLE.indexOf(currentStatus));
  const steps = LIFECYCLE.length;
  const isResolved = currentStatus === "Resolved" || currentStatus === "Closed";

  // Geometry — responsive via viewBox
  const W = 760, H = 160, padX = 40;
  const innerW = W - padX * 2;
  const stepX = (i) => padX + (innerW * i) / (steps - 1);
  const topY = (i) => {
    const t = i / (steps - 1);
    const e = Math.pow(t, 1.3);
    return 32 + e * 48; // 32 → 80 (centre)
  };
  const botY = (i) => {
    const t = i / (steps - 1);
    const e = Math.pow(t, 1.3);
    return 128 - e * 48; // 128 → 80 (centre)
  };

  // Build a smooth curved path through the points (quadratic between every two segments)
  const makePath = (yfn, upto = steps - 1) => {
    let d = `M ${stepX(0)} ${yfn(0)}`;
    for (let i = 1; i <= upto; i++) {
      const xp = stepX(i - 1); const yp = yfn(i - 1);
      const xc = stepX(i);     const yc = yfn(i);
      const cx = (xp + xc) / 2; // simple smoothing control x
      d += ` Q ${cx} ${yp}, ${xc} ${yc}`;
    }
    return d;
  };

  const topPath  = useMemo(() => makePath(topY), []); // eslint-disable-line react-hooks/exhaustive-deps
  const botPath  = useMemo(() => makePath(botY), []); // eslint-disable-line react-hooks/exhaustive-deps
  const topUpTo  = useMemo(() => makePath(topY, idx), [idx]); // eslint-disable-line react-hooks/exhaustive-deps
  const botUpTo  = useMemo(() => makePath(botY, idx), [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-sand-deep border hairline p-4 sm:p-6 lg:p-8" data-testid="case-lifecycle-stepper">
      <div className="flex items-baseline justify-between mb-4 sm:mb-6 gap-3">
        <h4 className="font-display text-xl sm:text-2xl text-court">The convergence</h4>
        <div className="eyebrow text-right">Two parties · one outcome</div>
      </div>

      <div className="w-full overflow-x-auto -mx-1">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[560px]" role="img" aria-label="Case lifecycle">
          <defs>
            <filter id="soft" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.6" />
            </filter>
          </defs>

          {/* Reference dashed midline */}
          <line x1={padX} y1={H/2} x2={W - padX} y2={H/2} stroke="#B8B0A0" strokeWidth="0.6" strokeDasharray="2 4" />

          {/* Full ghost tracks (the journey ahead) */}
          <path d={topPath} fill="none" stroke="#1F3A34" strokeOpacity="0.28" strokeWidth="1.2" strokeLinecap="round" />
          <path d={botPath} fill="none" stroke="#1F3A34" strokeOpacity="0.28" strokeWidth="1.2" strokeLinecap="round" />

          {/* Clay progress overlay — animated dashoffset draws the line in */}
          <path
            d={topUpTo} fill="none" stroke="#C2542A" strokeWidth="2.5" strokeLinecap="round"
            style={{ strokeDasharray: 1200, strokeDashoffset: 1200, animation: "esulah-draw 1.6s cubic-bezier(.2,.7,.3,1) forwards" }}
          />
          <path
            d={botUpTo} fill="none" stroke="#C2542A" strokeWidth="2.5" strokeLinecap="round"
            style={{ strokeDasharray: 1200, strokeDashoffset: 1200, animation: "esulah-draw 1.6s 0.1s cubic-bezier(.2,.7,.3,1) forwards" }}
          />

          {/* Resolved burst — only when reached */}
          {isResolved && (
            <g style={{ transformOrigin: `${stepX(steps - 1)}px 80px` }}>
              <circle cx={stepX(steps - 1)} cy={80} r="8" fill="#C2542A" opacity="0.18">
                <animate attributeName="r" values="8;22;8" dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.18;0;0.18" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx={stepX(steps - 1)} cy={80} r="5" fill="#C2542A">
                <animate attributeName="r" values="5;6.5;5" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <text x={stepX(steps - 1)} y={50} fill="#C2542A" fontFamily="Space Mono, monospace" fontSize="10" textAnchor="middle" letterSpacing="2">RESOLVED</text>
            </g>
          )}

          {/* Nodes */}
          {LIFECYCLE.map((step, i) => {
            const done = i < idx;
            const active = i === idx && !isResolved;
            const colorTop = done || active ? "#C2542A" : "#1F3A34";
            const fillNode = active ? "#C2542A" : done ? "#1F3A34" : "#EDE6D8";
            const r = active ? 5 : 3.5;
            return (
              <g key={step} style={{ animation: `esulah-fadein 0.4s ${0.6 + i * 0.06}s both` }}>
                <circle cx={stepX(i)} cy={topY(i)} r={r} fill={fillNode} stroke={colorTop} strokeWidth="1.2" />
                <circle cx={stepX(i)} cy={botY(i)} r={r} fill={fillNode} stroke={colorTop} strokeWidth="1.2" />
                {active && (
                  <line x1={stepX(i)} y1={topY(i)} x2={stepX(i)} y2={botY(i)} stroke="#C2542A" strokeWidth="0.6" strokeDasharray="2 2" />
                )}
              </g>
            );
          })}
        </svg>

        {/* Labels row aligned to nodes */}
        <div className="grid mt-3" style={{ gridTemplateColumns: `repeat(${steps}, minmax(0,1fr))` }}>
          {LIFECYCLE.map((step, i) => {
            const done = i < idx;
            const active = i === idx;
            return (
              <div key={step} className="text-center px-0.5 sm:px-1">
                <div className={`font-mono text-[9px] sm:text-[10px] tracking-[0.12em] uppercase ${active ? "text-clay" : done ? "text-court" : "text-stone"}`}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className={`mt-1 text-[10px] sm:text-[11px] leading-tight ${active ? "text-court font-semibold" : done ? "text-ink" : "text-stone"}`}>
                  {step}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity log */}
      <div className="mt-6 sm:mt-8 border-t hairline pt-5 sm:pt-6">
        <div className="eyebrow mb-3">Activity log</div>
        <ul className="space-y-2 max-h-[280px] overflow-auto pr-1 sm:pr-2">
          {[...events].reverse().map((e, i) => (
            <li key={i} className="grid grid-cols-[90px_1fr] sm:grid-cols-[112px_1fr] gap-3 text-sm border-b border-stone/40 pb-2 last:border-0">
              <div className="font-mono text-[11px] sm:text-xs text-stone">{new Date(e.at).toLocaleString("en-IN", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
              <div>
                <div className="text-ink text-[13px] sm:text-sm">{e.event}</div>
                <div className="text-stone text-[11px] mt-0.5">by {e.by}</div>
              </div>
            </li>
          ))}
          {events.length === 0 && <li className="text-sm text-stone">No activity yet.</li>}
        </ul>
      </div>

      <style>{`
        @keyframes esulah-draw { to { stroke-dashoffset: 0; } }
        @keyframes esulah-fadein { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          path[style*="esulah-draw"] { animation: none !important; stroke-dashoffset: 0 !important; }
          g[style*="esulah-fadein"] { animation: none !important; opacity: 1 !important; }
        }
      `}</style>
    </div>
  );
}
